import {
  type ComponentProps,
  forwardRef,
  type PropsWithChildren,
  type ReactNode,
} from "react";

import clsx from "clsx";
import { twMerge } from "tailwind-merge";

export interface InputButtonProps extends ComponentProps<"button"> {
  size?: "sm" | "md" | "lg";
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

export const InputButton = forwardRef<
  HTMLButtonElement,
  PropsWithChildren<InputButtonProps>
>(
  (
    {
      className,
      size = "sm",
      disabled,
      leftIcon,
      rightIcon,
      children,
      ...props
    },
    ref,
  ) => {
    return (
      <button
        ref={ref}
        className={twMerge(
          "group flex items-center justify-between gap-2",
          "bg-v2-primary border border-v2-secondary rounded text-sm text-v2-primary px-3",
          "hover:border-v2-primary hover:text-v2-primary",
          "focus:ring-2 focus:ring-v2-cosmic/200 focus:border-v2-cosmic/400",
          "focus:text-v2-primary",
          "data-[open]:ring-2 data-[open]:ring-v2-cosmic/200 data-[open]:border-v2-cosmic/400",
          "data-[open]:text-v2-primary",
          clsx({
            "h-8": size === "sm",
            "h-9": size === "md",
            "h-10": size === "lg",
            "pointer-events-none border-v2-tertiary text-v2-disabled": disabled,
          }),
          className,
        )}
        disabled={disabled}
        {...props}
      >
        {leftIcon && (
          <span
            className={twMerge(
              clsx("shrink-0 group-focus:text-v2-action", {
                "text-v2-disabled": disabled,
              }),
            )}
          >
            {leftIcon}
          </span>
        )}
        <span className="flex-1 text-left truncate">{children}</span>
        {rightIcon && (
          <span
            className={twMerge(
              clsx("shrink-0 group-focus:text-v2-action", {
                "text-v2-disabled": disabled,
              }),
            )}
          >
            {rightIcon}
          </span>
        )}
      </button>
    );
  },
);
